import { useCallback, useEffect, useState } from 'react';
import agent from '../../app/api/agent';
import { fieldChecked, fieldValue } from '../../app/components/polaris';
import type { KlaviyoSettings } from '../../app/models/KlaviyoSettings';
import { useStore } from '../../app/stores/store';
import PlanFeatureStatus from '../plan/PlanFeatureStatus';
import type { EditorStatus } from './editorStatus';

type Props = { onStatusChange?: (status: EditorStatus) => void };

// Klaviyo's public site ID is saved through the page's save bar.
export default function KlaviyoIntegrationSettings({ onStatusChange }: Props) {
    const { userStore } = useStore();
    const [saved, setSaved] = useState<KlaviyoSettings>();
    const [draft, setDraft] = useState<KlaviyoSettings>({ enabled: false, publicSiteId: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);
    useEffect(() => {
        let active = true;
        agent.Settings.klaviyoIntegration().then(value => {
            if (active) { setSaved(value); setDraft(value); }
        }).catch(() => { if (active) setError('Could not load Klaviyo settings. Reload this page to try again.'); });
        return () => { active = false; };
    }, []);
    const valid = /^[a-zA-Z0-9]{6}$/.test(draft.publicSiteId.trim());
    const needsSiteId = draft.enabled || !!draft.publicSiteId;
    const dirty = !!saved && JSON.stringify(saved) !== JSON.stringify(draft);
    const domain = userStore?.user?.shopDomain?.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const checkUrl = domain && /^[a-zA-Z0-9.-]+$/.test(domain) ? `https://${domain}/a/partfits/results?pf_klaviyo_check=1` : undefined;

    const save = useCallback(async () => {
        if (needsSiteId && !valid) { setTouched(true); return; }
        setSaving(true); setError('');
        try {
            const value = await agent.Settings.saveKlaviyoIntegration({ ...draft, publicSiteId: draft.publicSiteId.trim() });
            setSaved(value); setDraft(value); setTouched(false);
            window.shopify?.toast?.show?.(value.enabled ? 'Klaviyo settings saved' : 'Klaviyo integration turned off');
        } catch {
            setError('Could not save Klaviyo settings. Check the public site ID and try again.');
            throw new Error('Klaviyo settings could not be saved.');
        } finally { setSaving(false); }
    }, [draft, needsSiteId, valid]);
    const discard = useCallback(() => {
        if (saved) setDraft(saved);
        setError(''); setTouched(false);
    }, [saved]);

    useEffect(() => {
        onStatusChange?.({ hasChanges: dirty, loading: !saved && !error, saving, save, discard });
    }, [dirty, discard, error, onStatusChange, save, saved, saving]);

    const status = !saved ? (error ? 'Unable to load' : 'Checking') : saved.enabled ? 'On' : saved.publicSiteId ? 'Off' : 'Not set up';

    return (
        <s-section heading="Klaviyo">
            <s-stack gap="base">
                <s-text color="subdued">{status}</s-text>
                <PlanFeatureStatus feature="klaviyo" />
                {error && <s-banner tone="critical">{error}</s-banner>}
                <s-text-field label="Klaviyo public site ID" value={draft.publicSiteId} maxLength={6} placeholder="ABC123"
                    autocomplete="off" disabled={!saved || saving}
                    error={touched && needsSiteId && !valid ? 'Enter exactly six letters or numbers.' : undefined}
                    onInput={event => setDraft({ ...draft, publicSiteId: fieldValue(event) })}
                    onBlur={() => setTouched(true)} />
                <s-switch label="Send selections and garage changes to Klaviyo" checked={draft.enabled}
                    disabled={!saved || saving} onChange={event => setDraft({ ...draft, enabled: fieldChecked(event) })} />
                {saved?.enabled && checkUrl && <s-stack direction="inline">
                    <s-button href={checkUrl} target="_blank" disabled={dirty || saving}>Check connection on storefront</s-button>
                </s-stack>}
            </s-stack>
        </s-section>
    );
}
